import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import ThemeToggle from './ThemeToggle';

const Navigation = ({ onDemoClick, isDark, onThemeToggle }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Product', href: '#product' },
    { label: 'Evidence', href: '#evidence' },
    { label: 'DR & DME', href: '#dr-dme' },
    { label: 'ROI', href: '#roi' },
    { label: 'Live Demo', href: '#simulation' },
    { label: 'Compliance', href: '#compliance' },
    { label: 'Clinician Access', href: '#clinician-access' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? isDark 
            ? 'bg-slate-900/95 backdrop-blur-md border-b border-slate-800' 
            : 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <a 
            href="#" 
            className="flex items-center gap-2"
            data-testid="nav-logo"
          >
            <img 
              src="/skydoc-icon-transparent.png" 
              alt="SkyDoc" 
              className="h-8 md:h-9 w-auto"
            />
            <span className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              SkyDoc
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className={`text-sm font-medium transition-colors ${
                  isDark 
                    ? 'text-slate-400 hover:text-sky-400' 
                    : 'text-slate-600 hover:text-sky-600'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Desktop CTA + Theme Toggle */}
          <div className="hidden md:flex items-center gap-3">
            <ThemeToggle isDark={isDark} onToggle={onThemeToggle} />
            <button
              onClick={onDemoClick}
              className="px-5 py-2.5 bg-sky-500 text-white text-sm font-medium rounded-full hover:bg-sky-600 transition-colors"
              data-testid="nav-book-demo-btn"
            >
              Book Demo
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle isDark={isDark} onToggle={onThemeToggle} />
            <button
              className="p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`} />
              ) : (
                <Menu className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className={`md:hidden border-t ${
          isDark 
            ? 'bg-slate-900 border-slate-800' 
            : 'bg-white border-stone-200'
        }`}>
          <div className="px-4 py-3 space-y-1">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className={`block w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium ${
                  isDark 
                    ? 'text-slate-400 hover:bg-slate-800 hover:text-white' 
                    : 'text-slate-600 hover:bg-stone-50'
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                onDemoClick();
              }}
              className="w-full mt-3 px-5 py-2.5 bg-sky-500 text-white text-sm font-medium rounded-full hover:bg-sky-600 transition-colors"
              data-testid="mobile-book-demo-btn"
            >
              Book Demo
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
